/* Imperativt dialog-API för hela KanPlan (A9 i produktionsgenomgången 2026-09).

   Ersätter webbläsarens egna alert/confirm/prompt. Anropas från vilken fil som
   helst — React-komponenter såväl som vanliga JS-moduler (api-lager, hooks):

     if (!(await kpConfirm({ title: 'Ta bort posten?', message: 'Detta kan inte ångras.', variant: 'danger' }))) return;
     const orsak = await kpPrompt({ title: 'Avslå', label: 'Orsak', multiline: true });
     if (orsak === null) return;

   OBS: kpConfirm MÅSTE få await. Ett Promise räknas som sant i en if-sats, så
   utan await körs åtgärden innan frågan ställts. scripts/check_native_dialogs.py
   fäller bygget på kpConfirm( / kpPrompt( utan await, return eller .then.

   Providern (DialogProvider) registrerar sig här vid montering. Saknas den
   (t.ex. i ett isolerat Jest-test) svarar kpConfirm false och kpPrompt null —
   alltså "Avbryt" — och en varning loggas. Ingen fallback till webbläsarrutan. */

let handler = null;

/** @param {(request: object) => Promise<any>} fn */
export function registerDialogHandler(fn) {
  handler = fn;
  return () => { if (handler === fn) handler = null; };
}

export function hasDialogHandler() {
  return typeof handler === 'function';
}

function normalisera(opts, fallbackTitle) {
  if (typeof opts === 'string') return { title: opts };
  if (!opts || typeof opts !== 'object') return { title: fallbackTitle };
  return opts;
}

/**
 * Bekräftelsedialog (DS Del Ib). Löser till true vid bekräfta, false vid
 * Avbryt, Esc, klick utanför eller stängd provider.
 * @param {object|string} opts
 * @param {string} [opts.title]         Frågan, t.ex. "Ta bort utlägg #12?"
 * @param {string} [opts.message]       Konsekvensen, t.ex. "Utlägget raderas permanent."
 * @param {string} [opts.confirmLabel]  Primärknappens text (standard "OK", för danger "Ta bort")
 * @param {string} [opts.cancelLabel]   Standard "Avbryt"
 * @param {'primary'|'danger'|'warning'} [opts.variant]  Standard 'primary'
 * @param {string} [opts.requireText]   Kräv att användaren skriver exakt denna text (t.ex. "RADERA")
 * @returns {Promise<boolean>}
 */
export function kpConfirm(opts) {
  const o = normalisera(opts, 'Är du säker?');
  if (!handler) {
    warnNoProvider('kpConfirm');
    return Promise.resolve(false);
  }
  return handler({ kind: 'confirm', ...o }).then((v) => v === true);
}

/**
 * Textdialog. Löser till strängen vid bekräfta, null vid Avbryt/Esc/utanför.
 * @param {object|string} opts
 * @param {string} [opts.title]
 * @param {string} [opts.message]
 * @param {string} [opts.label]         Fältets etikett
 * @param {string} [opts.placeholder]
 * @param {string} [opts.defaultValue]
 * @param {boolean} [opts.multiline]    textarea i stället för input
 * @param {boolean} [opts.required]     Bekräfta spärrad tills fältet har innehåll
 * @param {number} [opts.maxLength]
 * @param {boolean} [opts.readOnly]     Visningsläge (t.ex. "kopiera länken")
 * @param {boolean} [opts.copyButton]   Visa Kopiera-knapp vid fältet
 * @param {string} [opts.requireText]   Extra fält där användaren måste skriva exakt denna text
 * @param {string} [opts.confirmLabel]
 * @param {string} [opts.cancelLabel]
 * @param {'primary'|'danger'|'warning'} [opts.variant]
 * @returns {Promise<string|null>}
 */
export function kpPrompt(opts) {
  const o = normalisera(opts, 'Ange värde');
  if (!handler) {
    warnNoProvider('kpPrompt');
    return Promise.resolve(null);
  }
  return handler({ kind: 'prompt', ...o }).then((v) => (typeof v === 'string' ? v : null));
}

function warnNoProvider(namn) {
  if (typeof console !== 'undefined' && typeof console.warn === 'function') {
    console.warn(`${namn} anropad utan DialogProvider — svarar som Avbryt. Montera <DialogProvider> i App.js.`);
  }
}

/* ── Esc-register (A9b) ──
   Problem: en dialog som öppnas OVANPÅ en modal/förhandsvisning som själv lyssnar på
   window i capture-fasen får Esc EFTER den (lyssnare körs i registreringsordning), så
   första Esc stängde det underliggande. Lösning: DialogProvider registrerar EN
   window-capture-lyssnare redan vid appstart (före alla moduler) och skickar Esc till
   den dialog som senast öppnats. Dialogerna anmäler sig här vid montering. */
const escStack = [];
let escDispatcherMounted = false;

export function pushEscHandler(fn) {
  escStack.push(fn);
  return () => { const i = escStack.lastIndexOf(fn); if (i >= 0) escStack.splice(i, 1); };
}

export function hasEscDispatcher() { return escDispatcherMounted; }
export function setEscDispatcherMounted(v) { escDispatcherMounted = !!v; }

/** Anropas av DialogProvider i window-capture. Returnerar true om en dialog tog Esc. */
export function dispatchEscape(e) {
  if (e.key !== 'Escape' || escStack.length === 0) return false;
  // stopImmediatePropagation: även andra lyssnare på SAMMA mål (window) ska missa Esc.
  e.stopImmediatePropagation(); e.stopPropagation(); e.preventDefault();
  escStack[escStack.length - 1]();
  return true;
}
